import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Section, Eyebrow } from "@/components/Section";
import { Lock, Loader2, ArrowLeft, AlertCircle } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Section testId="admin-login" className="!pt-24 !pb-32">
      <div className="max-w-md mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-body text-[#6B8E7B] hover:text-[#557564]" data-testid="admin-back-home">
          <ArrowLeft size={14} /> Back to site
        </Link>

        <div className="mt-10 bg-white rounded-[2rem] border border-[#E8E5DF] p-10">
          <span className="h-12 w-12 inline-flex items-center justify-center rounded-full bg-[#F0EDE6] text-[#6B8E7B]">
            <Lock size={20} />
          </span>
          <Eyebrow className="mt-6">Admin</Eyebrow>
          <h1 className="font-serif-display text-3xl sm:text-4xl mt-3 text-[#2E3630] leading-tight">
            Practice dashboard.
          </h1>
          <p className="font-body mt-3 text-sm text-[#5F6B62]">
            Sign in to review bookings and inquiries.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-6" data-testid="admin-login-form">
            <label className="block">
              <span className="eyebrow text-[10px]">Username</span>
              <input
                type="text"
                data-testid="admin-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                className="input-underline"
                required
              />
            </label>
            <label className="block">
              <span className="eyebrow text-[10px]">Password</span>
              <input
                type="password"
                data-testid="admin-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="input-underline"
                required
              />
            </label>

            {error && (
              <div className="flex items-start gap-2 rounded-2xl bg-[#FBEDEB] border border-[#E8C4BF] p-4" data-testid="admin-login-error">
                <AlertCircle size={16} className="text-[#B06E66] mt-0.5 shrink-0" />
                <p className="text-sm text-[#7C4A45] font-body">{error}</p>
              </div>
            )}

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={submitting || !username || !password}
              className="btn-pill btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <><Loader2 size={16} className="animate-spin" /> Signing in…</>
              ) : (
                <>Sign in</>
              )}
            </button>
          </form>
        </div>
      </div>
    </Section>
  );
}
